import moment from 'moment';
import React, { useCallback } from 'react';
import { PiCalendar, PiCurrencyEur, PiPackage } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { Input } from 'rizzui';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import CustomSpinner from '../../../../../loader/CustomSpinner';
import { PickupService } from '../../../../../services/pickup/pickupService';
import { PickupProduct } from '../../../../../type/entities/pickup-product-type';
import { PickupApiResponse } from '../../../../../type/entities/pickup-type';

interface PickupProductsProps { }

const PickupProducts: React.FC<PickupProductsProps> = () => {

    // HOOKS
    const { id = '' } = useParams<{ id: string }>();
    const service = new PickupService();

    // DATA FETCHING

    // -----------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS DATOS DE LA RECOGIDA POR ID INCLUYENDO PRODUCTOS
     * @EN GETS THE PICKUP DATA BY ID INCLUDING PRODUCTS
     */
    // -----------------------------------------------------------------------------------------------------
    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getPickupById(id);
        return response.getResponseData() as PickupApiResponse;
    }, [id]));
    // -----------------------------------------------------------------------------------------------------

    // LOADING STATE
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <CustomSpinner />
            </div>
        );
    }

    // NO DATA STATE
    if (!data || !data.pickupProducts || data.pickupProducts.length === 0) {
        return (
            <div className="@container">
                <div className="text-center py-12">
                    <PiPackage className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No hay productos registrados
                    </h3>
                    <p className="text-gray-500">
                        Esta recogida no tiene productos entregados o recogidos registrados.
                    </p>
                </div>
            </div>
        );
    }

    // RENDER
    return (
        <div className="@container">
            <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                <FormGroup
                    title="Productos Entregados"
                    description={`Total de ${data.pickupProducts.length} producto${data.pickupProducts.length !== 1 ? 's' : ''} registrado${data.pickupProducts.length !== 1 ? 's' : ''}`}
                    className='pt-4'
                    titleCols="@md:col-span-2"
                    childCols="@md:col-span-10"
                >
                    <div className="space-y-6">
                        {data.pickupProducts.map((pickupProduct: PickupProduct, index: number) => (
                            <div
                                key={pickupProduct.id}
                                className="border border-gray-200 rounded-lg p-6 bg-gray-50"
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <PiPackage className="h-5 w-5 text-blue-600" />
                                    <h4 className="text-lg font-medium text-gray-900">
                                        Producto #{index + 1}
                                    </h4>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    <Input
                                        disabled
                                        label="Nombre del Producto"
                                        value={pickupProduct.product?.name ?? ""}
                                        className="col-span-1"
                                    />

                                    <Input
                                        disabled
                                        label="Categoría"
                                        value={pickupProduct.product?.category?.name ?? ""}
                                        className="col-span-1"
                                    />

                                    <Input
                                        disabled
                                        label="Cantidad"
                                        value={pickupProduct.quantity?.toString() ?? "0"}
                                        className="col-span-1"
                                    />

                                    <Input
                                        disabled
                                        prefix={<PiCurrencyEur className="h-4 w-4 text-gray-500" />}
                                        label="Precio Total"
                                        value={pickupProduct.totalPrice ? `${pickupProduct.totalPrice} €` : "No especificado"}
                                        className="col-span-1"
                                    />

                                    <Input
                                        disabled
                                        prefix={<PiCalendar className="h-4 w-4 text-gray-500" />}
                                        label="Fecha de Registro"
                                        value={pickupProduct.createdAt ? moment(pickupProduct.createdAt.date).format('DD/MM/YYYY HH:mm') : ""}
                                        className="col-span-1"
                                    />

                                    {pickupProduct.updatedAt && (
                                        <Input
                                            disabled
                                            prefix={<PiCalendar className="h-4 w-4 text-gray-500" />}
                                            label="Última Actualización"
                                            value={moment(pickupProduct.updatedAt.date).format('DD/MM/YYYY HH:mm')}
                                            className="col-span-1"
                                        />
                                    )}
                                </div>

                                {pickupProduct.product?.description && (
                                    <div className="mt-4">
                                        <Input
                                            disabled
                                            label="Descripción del Producto"
                                            value={pickupProduct.product.description}
                                            className="w-full"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </FormGroup>

                {/* Resumen de productos */}
                <FormGroup
                    title="Resumen"
                    description="Información consolidada de los productos"
                    className='pt-4'
                    titleCols="@md:col-span-2"
                    childCols="@md:col-span-10 md:grid-cols-3"
                >
                    <Input
                        disabled
                        label="Total de Productos"
                        value={data.pickupProducts.length.toString()}
                        className="md:col-span-1"
                    />

                    <Input
                        disabled
                        label="Cantidad Total"
                        value={data.pickupProducts.reduce((sum: number, product: PickupProduct) => sum + (product.quantity || 0), 0).toString()}
                        className="md:col-span-1"
                    />

                    <Input
                        disabled
                        prefix={<PiCurrencyEur className="h-4 w-4 text-gray-500" />}
                        label="Valor Total"
                        value={`${(
                            data.pickupProducts.reduce((sum: number, product: PickupProduct) => {
                                const price = parseFloat(product.totalPrice as any); // convierte string a número
                                return sum + (isNaN(price) ? 0 : price); // si no es un número, suma 0
                            }, 0)
                        ).toFixed(2)} €`}
                        className="md:col-span-1"
                    />
                </FormGroup>
            </div>
        </div>
    );
};

export default PickupProducts;